<template>
  <div class="zt-zoom-image w-full h-full relative">
    <div
      ref="container"
      class="absolute overflow-hidden w-full h-full flex flex-col justify-center items-center"
    >
      <img v-if="hasImageUrl" ref="image" :alt="alt" :src="imgUrl" />
      <zt-icon
        v-if="!hasImageUrl"
        name="image"
        class="text-zt-dark-200"
        :style="{ fontSize: windowResize.width <= 1366 ? '7rem' : '10rem' }"
      />
    </div>
    <div
      v-if="hasImageUrl"
      class="zoom-controls flex flex-wrap w-16 absolute z-10"
    >
      <zt-button
        data-vs-button="zoom-in"
        class="mb-2"
        color="dark-100"
        icon="plus-2"
        fab
        outlined
        @click="zoomTo('in')"
      >
      </zt-button>
      <zt-button
        data-vs-button="zoom-out"
        class="mt-2"
        color="dark-100"
        icon="minus"
        fab
        outlined
        @click="zoomTo('out')"
      >
      </zt-button>
    </div>
  </div>
</template>
<script>
import { clamp } from 'lodash'
import { useWindowResize } from '@sichern/ui-elements'
export default {
  name: 'ztZoom',
  props: {
    alt: {
      type: String,
      default: '',
    },
    imgUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      cssTransform3d: '',
      increment: 0.20,
      isDragging: false,
      minX: 0,
      minY: 0,
      minZoom: 0.2,
      maxX: 0,
      maxY: 0,
      maxZoom: 4,
      positionX: 0,
      positionY: 0,
      scale: .80,
      scaleFactor: 0.1,
      startX: 0,
      startY: 0,
      windowResize: useWindowResize(this)
    }
  },
  watch: {
    windowResize: {
      handler() {
        if (this.hasImageUrl) {
          this.updateCssTransform()
          this.updateMaxLimit()
        }
      },
      deep: true
    },
  },
  computed: {
    hasImageUrl() {
      return this.imgUrl !== ''
    }
  },
  mounted() {
    this.setup()
  },
  methods: {
    onDrag(event) {
      event.preventDefault()
      if (!this.isDragging) return
      const deltaX = this.startX - event.clientX
      const deltaY = this.startY - event.clientY
      this.$refs.image.style.cursor = 'grabbing'
      this.positionX = clamp(this.positionX - deltaX, this.minX, this.maxX)
      this.positionY = clamp(this.positionY - deltaY, this.minY, this.maxY)
      this.startX = event.clientX
      this.startY = event.clientY
      this.updateMaxLimit()
      this.updateCssTransform()
    },
    onDragEnd() {
      this.isDragging = false
      this.$refs.image.style.cursor = 'grab'
      this.updateMaxLimit()
      this.updateCssTransform()
    },
    onDragStart(event) {
      event.preventDefault()
      this.isDragging = true
      this.$refs.image.style.cursor = 'grabbing'
      this.startX = event.clientX
      this.startY = event.clientY
      this.updateMaxLimit()
      this.updateCssTransform()
    },
    onWheel(event) {
      event.preventDefault()
      const previousScale = this.scale
      const delta = Math.sign(-event.deltaY)
      const calculatedScale = this.scale * Math.exp(delta * this.scaleFactor)
      this.scale = clamp(calculatedScale, this.minZoom, this.maxZoom)
      this.updateMaxLimit()
      if (this.scale > 1 && this.scale < previousScale) {
        this.adjustPosition()
      }
      this.updateCssTransform()
    },
    adjustPosition() {
      if (this.positionX)  {
        this.positionX -= this.positionX > 0 ? (this.positionX - this.maxX) : (this.positionX - this.minX)
      }
      if (this.positionY)  {
        this.positionY -= this.positionY > 0 ? (this.positionY - this.maxY) : (this.positionY - this.minY)
      }
    },
    updateMaxLimit() {
      const container = this.$refs.container
      const image = this.$refs.image
      const totalMaxX = Math.max(
        0,
        Math.round(image.offsetWidth * this.scale) - container.offsetWidth
      )
      const totalMaxY = Math.max(
        0,
        Math.round(image.offsetHeight * this.scale) - container.offsetHeight
      )
      this.maxX = Math.round((totalMaxX / 2) / this.scale)
      this.maxY = Math.round((totalMaxY / 2) / this.scale)
      this.minX = 0 - this.maxX
      this.minY = 0 - this.maxY
    },
    updateCssTransform() {
      this.cssTransform3d = `scale(${this.scale}) translate3d(${this.positionX}px, ${this.positionY}px, 0)`
      this.$refs.image.style.transform = this.cssTransform3d
    },
    setup() {
      if (!this.hasImageUrl) return
      const container = this.$refs.container
      container.addEventListener('wheel', this.onWheel, false)
      container.addEventListener('mousedown', this.onDragStart, false)
      container.addEventListener('mousemove', this.onDrag, false)
      container.addEventListener('mouseup', this.onDragEnd, false)
      container.addEventListener('mouseleave', this.onDragEnd, false)
      this.updateMaxLimit()
      this.updateCssTransform()
    },
    zoomTo(zoomType) {
      const previousScale = this.scale
      if (zoomType === 'in' && this.scale < this.maxZoom) this.scale += this.increment
      if (zoomType === 'out' && this.scale > this.minZoom) this.scale -= this.increment
      this.updateMaxLimit()
      if (this.scale > 1 && this.scale < previousScale) {
        this.adjustPosition()
      }
      this.updateCssTransform()
    },
    beforeDestroy() {
      const container = this.$refs.container
      container.removeEventListener('wheel', this.onWheel)
      container.removeEventListener('mousedown', this.onDragStart)
      container.removeEventListener('mousemove', this.onDrag)
      container.removeEventListener('mouseup', this.onDragEnd)
      container.removeEventListener('mouseleave', this.onDragEnd)
    }
  },
}
</script>
<style scoped lang="scss">
.zt-zoom-image img {
  display: block;
  touch-action: none;
  max-width: 100%;
  max-height: 100%;
}
.zt-zoom-image .zoom-controls {
  bottom: 1.5rem;
  right: 1.5rem;
  opacity: 0;
  transition: all 300ms cubic-bezier(0.42, 0, 1, 1); /* ease-in */
  transition-timing-function: cubic-bezier(0.42, 0, 1, 1); /* ease-in */
}
.zt-zoom-image:hover {
  .zoom-controls {
    opacity: 1;
  }
}
::v-deep {
  .zt-button-fab {
    color: #707070 !important;
    height: 35px;
    width: 35px;
  }
  .zt-icon-minus {
    font-size: 1.5rem !important;
  }
  .zt-icon-plus-2 {
    font-size: 1.5rem !important;
  }
}
</style>